/** 按自动规则调用平台接口：实拍图 / 证书 / 代理自动绑定（后端 Job + 多店抢 QPS） */

import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import {
  CERT_AUTO_BIND_ENABLED_PREF_KEY,
  PHOTO_AUTO_BIND_ENABLED_PREF_KEY,
  readCertAutoBindEnabled,
  readPhotoAutoBindEnabled,
  saveComplianceAutoBindErrors,
} from '@/shein-platform/modules/compliance/complianceAutoBindRules';

function emptySummary() {
  return { ok: 0, skip: 0, fail: 0, errors: [], errorsBySkc: {}, boundSkcs: [] };
}

function parseSkcError(message) {
  const text = String(message || '').trim();
  if (!text) return null;
  const idx = text.indexOf('：');
  if (idx <= 0) return null;
  const skc = text.slice(0, idx).trim();
  const reason = text.slice(idx + 1).trim();
  if (!skc || !reason) return null;
  return { skc, reason };
}

async function persistBindErrors(shopId, kind, summary) {
  const errorsBySkc = { ...(summary.errorsBySkc || {}) };
  (summary.errors || []).forEach((msg) => {
    const parsed = parseSkcError(msg);
    if (parsed) errorsBySkc[parsed.skc] = parsed.reason;
  });
  summary.errorsBySkc = errorsBySkc;
  try {
    await saveComplianceAutoBindErrors({
      getPreferences: () => sheinPlatformAPI.getPreferences(),
      updatePreferences: (body) => sheinPlatformAPI.updatePreferences(body),
      shopId,
      kind,
      errorsBySkc,
      clearSkcs: summary.boundSkcs || [],
    });
  } catch {
    /* 错误日志持久化失败不影响主流程 */
  }
  return summary;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function isJobTerminal(view) {
  if (!view) return false;
  if (view.done) return true;
  const status = String(view.status || '').toLowerCase();
  return status === 'success' || status === 'failed' || status === 'cancelled';
}

/**
 * 启动后端 Job 并轮询至结束（与产品模板批量复制同一套店铺抢 QPS 模型）。
 */
async function waitForAutoBindJob({
  shopId,
  start,
  get,
  onProgress,
  pollMs = 600,
}) {
  const started = await start();
  const jobId = String(started?.jobId || '').trim();
  if (!jobId) {
    throw new Error('未能创建自动绑定任务');
  }
  onProgress?.({
    current: Number(started?.processed) || 0,
    total: Math.max(1, Number(started?.total) || 1),
    message: started?.message || '排队中…',
  });

  let view = started;
  while (!isJobTerminal(view)) {
    await sleep(pollMs);
    view = await get(jobId);
    onProgress?.({
      current: Number(view?.processed) || 0,
      total: Math.max(1, Number(view?.total) || 1),
      message: view?.message || '',
    });
  }
  return view || {};
}

function summaryFromJobView(view) {
  const summary = emptySummary();
  summary.ok = Number(view?.ok ?? view?.imported) || 0;
  summary.skip = Number(view?.skip) || 0;
  summary.fail = Number(view?.fail) || 0;
  if (Array.isArray(view?.boundSkcs)) {
    summary.boundSkcs = view.boundSkcs.map((s) => String(s || '').trim()).filter(Boolean);
  }
  if (Array.isArray(view?.errors)) {
    summary.errors.push(...view.errors.filter(Boolean));
  } else if (view?.error) {
    summary.errors.push(String(view.error));
  } else if (String(view?.status || '').toLowerCase() === 'failed' && view?.message) {
    summary.errors.push(String(view.message));
  }
  return summary;
}

/**
 * 对待补充实拍图 SKC：后端按款号匹配并按 skc-save-label QPS 并行绑定。
 * @returns {{ ok: number, skip: number, fail: number, errors: string[], errorsBySkc: Record<string,string>, boundSkcs: string[] }}
 */
export async function runPhotoAutoBind(shopId, { onProgress, force = false } = {}) {
  const summary = emptySummary();
  if (!shopId) {
    summary.errors.push('缺少店铺');
    return summary;
  }
  if (!force) {
    const prefRes = await sheinPlatformAPI.getPreferences().catch(() => null);
    const prefs = prefRes?.preferences || prefRes || {};
    if (!readPhotoAutoBindEnabled(prefs, shopId)) {
      return summary;
    }
  }
  try {
    const view = await waitForAutoBindJob({
      shopId,
      start: () => sheinPlatformAPI.startPhotoAutoBindJob(shopId),
      get: (jobId) => sheinPlatformAPI.getPhotoAutoBindJob(shopId, jobId),
      onProgress,
    });
    Object.assign(summary, summaryFromJobView(view));
  } catch (e) {
    summary.fail += 1;
    summary.errors.push(e?.message || '实拍图自动绑定失败');
  }
  return persistBindErrors(shopId, 'photo', summary);
}

/**
 * 对未审核/缺失证书：后端按款号匹配证书库并按 bind QPS 并行绑定。
 */
export async function runCertAutoBind(shopId, { maxRows = 200, onProgress, force = false } = {}) {
  const summary = emptySummary();
  if (!shopId) {
    summary.errors.push('缺少店铺');
    return summary;
  }
  if (!force) {
    const prefRes = await sheinPlatformAPI.getPreferences().catch(() => null);
    const prefs = prefRes?.preferences || prefRes || {};
    if (!readCertAutoBindEnabled(prefs, shopId)) {
      return summary;
    }
  }
  try {
    const view = await waitForAutoBindJob({
      shopId,
      start: () => sheinPlatformAPI.startCertAutoBindJob(shopId, { maxRows }),
      get: (jobId) => sheinPlatformAPI.getCertAutoBindJob(shopId, jobId),
      onProgress,
    });
    Object.assign(summary, summaryFromJobView(view));
  } catch (e) {
    summary.fail += 1;
    summary.errors.push(e?.message || '证书自动绑定失败');
  }
  return persistBindErrors(shopId, 'certificate', summary);
}

/**
 * 对未绑定代理 SKC：后端按规则匹配并按 save-skc-agency QPS 并行绑定。
 */
export async function runAgencyAutoBind(shopId, { maxRows = 80, onProgress } = {}) {
  const summary = emptySummary();
  if (!shopId) {
    summary.errors.push('缺少店铺');
    return summary;
  }
  try {
    const view = await waitForAutoBindJob({
      shopId,
      start: () => sheinPlatformAPI.startAgencyAutoBindJob(shopId, { maxRows }),
      get: (jobId) => sheinPlatformAPI.getAgencyAutoBindJob(shopId, jobId),
      onProgress,
    });
    Object.assign(summary, summaryFromJobView(view));
  } catch (e) {
    summary.fail += 1;
    summary.errors.push(e?.message || '代理自动绑定失败');
  }
  return persistBindErrors(shopId, 'agency', summary);
}

/** 同步完成后：若开关开启则自动跑一轮 */
export async function maybeRunPhotoAutoBindAfterSync(shopId) {
  if (!shopId) return emptySummary();
  const prefRes = await sheinPlatformAPI.getPreferences().catch(() => null);
  const prefs = prefRes?.preferences || prefRes || {};
  if (!readPhotoAutoBindEnabled(prefs, shopId)) return emptySummary();
  return runPhotoAutoBind(shopId, { force: true });
}

export async function maybeRunCertAutoBindAfterSync(shopId) {
  if (!shopId) return emptySummary();
  const prefRes = await sheinPlatformAPI.getPreferences().catch(() => null);
  const prefs = prefRes?.preferences || prefRes || {};
  if (!readCertAutoBindEnabled(prefs, shopId)) return emptySummary();
  return runCertAutoBind(shopId, { force: true });
}

// re-export keys used by modal save path
export { PHOTO_AUTO_BIND_ENABLED_PREF_KEY, CERT_AUTO_BIND_ENABLED_PREF_KEY };
